import { gql } from "@apollo/client";
import { CARD_PROGRAM_CAPABILITY_FIELDS } from "./cardProgram";

export const GET_CARD_DETAIL = gql`
  query card($id: ID!, $selectedCompanyId: ID) {
    card(id: $id, selectedCompanyId: $selectedCompanyId) {
      id
      cardNumber
      nickname
      cardProgram {
        id
        nickname
      }
      type
      shippingSpeed
      user {
        id
        firstName
        lastName
        profilePicUrl
        email
        userId
        acceptedTerms
      }
      company {
        id
        businessLegalName
      }
      department {
        id
        name
      }
      account {
        id
        name
        url
      }
      status
      pendingChange {
        requestedAt
      }
      customFields {
        fieldSettingId
        values
      }
      activePolicy {
        id
        status
        card_expiration_date
        card_cancellation_date
        card_activation_date
        transaction_limit
        daily_limit
        weekly_limit
        monthly_limit
        total_limit
        receipt_policy
        allocation_policy
        expenseCategoryData {
          id
          name
        }
        allocationData {
          key
          name
          id
          allocationCode
        }
        merchants {
          id
          name
          logo
          cachedLogo
        }
        allowed_categories {
          id
          name
        }
        blocked_merchants {
          id
          name
          logo
          cachedLogo
        }
        blocked_categories {
          id
          name
        }
      }
      spendingSummary {
        dailySpent
        weeklySpent
        monthlySpent
        yearlySpent
        totalSpent
      }
      currency
      averageTransaction
      complianceRecord
      pendingRequest {
        id
        requestStatus
        requestType
        approval {
          manager {
            approvalStatus
            isRequired
          }
          admin {
            approvalStatus
            isRequired
          }
        }
        approverIds
        approvedBy {
          userId
          userName
          role
        }
        isApprovalAllowed
        isDenyAllowed
        requestData {
          id
          status
          card_expiration_date
          card_cancellation_date
          card_activation_date
          transaction_limit
          daily_limit
          weekly_limit
          monthly_limit
          total_limit
          receipt_policy
          allocation_policy
          expenseCategoryData {
            id
            name
          }
          allocationData {
            key
            name
            id
            allocationCode
          }
          merchants {
            id
            name
            logo
            cachedLogo
          }
          allowed_categories {
            id
            name
          }
          blocked_merchants {
            id
            name
            logo
            cachedLogo
          }
          blocked_categories {
            id
            name
          }
        }
      }
      shipping {
        name
        address {
          line1
          line2
          city
          state
          postal_code
          country
        }
        carrier
        status
        eta
        trackingNumber
        trackingUrl
      }
      cardProgram {
        id
        ...CardProgramCapabilityFields
      }
    }
  }
  ${CARD_PROGRAM_CAPABILITY_FIELDS}
`;

export const GET_CARDS = gql`
  query cards(
    $selectedCompanyId: ID
    $financialAccountId: ID
    $issuer: String
    $currency: String
    $console: Boolean! = false
    $cardStatus: [CardStatus]
    $cardType: [CardType]
    $cardLimit: CardLimitQuery
    $accountId: ID
    $userId: ID
    $mine: Boolean
    $searchTerm: String
    $first: Int
    $after: String
    $before: String
  ) {
    cards(
      selectedCompanyId: $selectedCompanyId
      financialAccountId: $financialAccountId
      issuer: $issuer
      currency: $currency
      cardStatus: $cardStatus
      cardType: $cardType
      cardLimit: $cardLimit
      accountId: $accountId
      userId: $userId
      mine: $mine
      searchTerm: $searchTerm
      first: $first
      after: $after
      before: $before
    ) {
      edges {
        cursor
        node {
          id
          consoleProgram @include(if: $console) {
            id
            name
            issuer
          }
          cardNumber
          nickname
          type
          status
          user {
            id
            firstName
            lastName
            profilePicUrl
            acceptedTerms
          }
          account {
            id
            name
            url
            status
          }
          status
          activePolicy {
            id
            card_expiration_date
            card_cancellation_date
            transaction_limit
            daily_limit
            weekly_limit
            monthly_limit
            total_limit
            merchants {
              id
              name
            }
            allowed_categories {
              id
              name
            }
            blocked_merchants {
              id
              name
            }
            blocked_categories {
              id
              name
            }
          }
          # No id, so Apollo replaces it whole: it keeps GET_CARD_DETAIL's fields or it strips the drawer's copy.
          spendingSummary {
            dailySpent
            weeklySpent
            monthlySpent
            yearlySpent
            totalSpent
          }
          pendingRequest {
            id
            requestType
            requestData {
              id
              card_expiration_date
              card_cancellation_date
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const GET_PENDING_APPROVAL_CARD = gql`
  query pendingCardApproval($searchTerm: String, $first: Int, $after: String) {
    pendingCardApproval(searchTerm: $searchTerm, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          cardNumber
          type
          currency
          user {
            id
            firstName
            lastName
            email
            acceptedTerms
          }
          company {
            id
            businessLegalName
          }
          department {
            id
            name
          }
          status
          activePolicy {
            id
            status
            card_expiration_date
            card_cancellation_date
            card_activation_date
            transaction_limit
            daily_limit
            weekly_limit
            monthly_limit
            total_limit
            receipt_policy
            allocation_policy
            expenseCategoryData {
              id
              name
            }
            allocationData {
              key
              name
              id
              allocationCode
            }
            merchants {
              id
              name
              logo
            }
            allowed_categories {
              id
              name
            }
            blocked_merchants {
              id
              name
              logo
              cachedLogo
            }
            blocked_categories {
              id
              name
            }
          }
          pendingRequest {
            id
            requestStatus
            requestType
            requestDate
            requestData {
              id
              status
              card_expiration_date
              card_cancellation_date
              card_activation_date
              transaction_limit
              daily_limit
              weekly_limit
              monthly_limit
              total_limit
              receipt_policy
              allocation_policy
              expenseCategoryData {
                id
                name
              }
              allocationData {
                key
                name
                id
                allocationCode
              }
              merchants {
                id
                name
                logo
              }
              allowed_categories {
                id
                name
              }
              blocked_merchants {
                id
                name
                logo
                cachedLogo
              }
              blocked_categories {
                id
                name
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

/** The pending queue size alone: the full document selects the whole card node. */
export const GET_PENDING_APPROVAL_CARD_COUNT = gql`
  query pendingCardApprovalCount($searchTerm: String) {
    pendingCardApproval(searchTerm: $searchTerm) {
      pageInfo {
        total
      }
    }
  }
`;

export const CREATE_CARD_EPHEMERAL_KEY = gql`
  mutation createCardEphemeralKey($id: ID) {
    createCardEphemeralKey(id: $id) {
      token
    }
  }
`;

export const GET_CARD_EPHEMERAL_KEY = gql`
  query getCardEphemeralKey($token: String) {
    getCardEphemeralKey(token: $token) {
      verified
      data {
        issuerId
        accountId
        publishableKey
        cardIssuer
        key {
          id
          secret
        }
      }
    }
  }
`;

export const ADD_NEW_CARD = gql`
  mutation requestNewCard($input: AddCardInput) {
    requestNewCard(input: $input) {
      id
      cardNumber
      type
      company {
        id
        businessLegalName
      }
      department {
        id
        name
      }
      status
    }
  }
`;

export const UPDATE_CARD_NICKNAME = gql`
  mutation updateCardNickname($id: ID!, $nickname: String!) {
    updateCardNickname(id: $id, nickname: $nickname) {
      id
      nickname
    }
  }
`;

export const UPDATE_CARD_SHIPPING_SPEED = gql`
  mutation updateCardShippingSpeed(
    $id: ID!
    $shippingSpeed: CardShippingSpeed!
  ) {
    updateCardShippingSpeed(id: $id, shippingSpeed: $shippingSpeed) {
      id
      shippingSpeed
    }
  }
`;

export const GET_MERCHANTS = gql`
  query merchants {
    merchants {
      id
      name
    }
  }
`;

export const MERCHANT_NAME_SEARCH = gql`
  query merchantNameSearch($searchValue: String!, $searchType: SearchType!) {
    merchantNameSearch(searchValue: $searchValue, searchType: $searchType) {
      id
      name
      logo
      cachedLogo
      url
    }
  }
`;

export const REQUEST_UPDATE_CARD = gql`
  mutation requestUpdateCard($id: ID!, $input: PolicyInput!) {
    requestUpdateCard(id: $id, input: $input) {
      id
      pendingRequest {
        id
      }
    }
  }
`;

export const APPROVE_CARD_REQUEST = gql`
  mutation approveCardRequest(
    $id: ID!
    $cardRequestId: ID!
    $input: PolicyInput
  ) {
    approveCardRequest(id: $id, cardRequestId: $cardRequestId, input: $input) {
      id
    }
  }
`;

export const DENY_CARD_REQUEST = gql`
  mutation denyCardRequest($id: ID!, $cardRequestId: ID!) {
    denyCardRequest(id: $id, cardRequestId: $cardRequestId) {
      id
    }
  }
`;

export const APPROVE_CARD = gql`
  mutation approveCard($id: ID!) {
    approveCard(id: $id) {
      id
    }
  }
`;

export const UNFREZEE_CARD = gql`
  mutation unFreezeCard($id: ID!) {
    unFreezeCard(id: $id) {
      id
      status
    }
  }
`;

export const DENY_CARD = gql`
  mutation denyCard($id: ID!) {
    denyCard(id: $id) {
      id
      status
    }
  }
`;

export const FREEZE_CARD = gql`
  mutation freezeCard($id: ID!) {
    freezeCard(id: $id) {
      id
      status
    }
  }
`;

export const ACTIVATE_PHYSICAL_CARD = gql`
  mutation activatePhysicalCard($last4: String!) {
    activatePhysicalCard(last4: $last4)
  }
`;

export const GET_EXTERNAL_PENDING_CARDS = gql`
  query pendingExternalCardDetails($accountId: String) {
    pendingExternalCardDetails(accountId: $accountId) {
      cardIssuerId
      cardNumber
      nickname
      ownerName
      ownerEmail
      user {
        id
        firstName
        lastName
      }
      possibleCards {
        id
        nickname
        cardNumber
        status
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const GET_ASTRADA_EXTERNAL_PENDING_CARDS = gql`
  query pendingAstradaCardDetails {
    pendingAstradaCardDetails {
      cardIssuerId
      cardNumber
      nickname
      ownerName
      ownerEmail
      user {
        id
        firstName
        lastName
      }
      possibleCards {
        id
        nickname
        cardNumber
        status
        user {
          id
          firstName
          lastName
        }
      }
      account {
        id
        name
        astradaCard {
          network
        }
      }
      id
    }
  }
`;

export const ADD_EXTERNAL_CARDS = gql`
  mutation addExternalCards(
    $accountId: String
    $startDate: Float
    $input: [AddExternalCardInput]
  ) {
    addExternalCards(
      accountId: $accountId
      startDate: $startDate
      input: $input
    ) {
      id
      cardIssuerId
    }
  }
`;

export const ADD_ASTRADA_EXTERNAL_CARDS = gql`
  mutation addAstradaExternalCards(
    $accountId: String
    $input: [AddAstradaCardInput]
  ) {
    addAstradaExternalCards(accountId: $accountId, input: $input) {
      id
      cardIssuerId
    }
  }
`;

// The dashboard's cards. Card mutations refetch this by document; refetching GET_CARDS would reset the paged list to page one.
export const MY_CARDS = gql`
  query myCards($cardStatus: [CardStatus]) {
    cards(mine: true, cardStatus: $cardStatus) {
      edges {
        node {
          id
          nickname
          type
          cardNumber
          currency
          status
          spendingSummary {
            dailySpent
            weeklySpent
            monthlySpent
            yearlySpent
            totalSpent
          }
          activePolicy {
            id
            transaction_limit
            daily_limit
            weekly_limit
            monthly_limit
            total_limit
          }
        }
      }
    }
  }
`;
